import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { Calendar, MapPin, Filter, Search, Edit2, Trash2, Plus, Navigation, X, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mileage`;

const TripList = () => {
  const currentDate = new Date();
  const currentMonth = (currentDate.getMonth() + 1).toString();
  const currentYear = currentDate.getFullYear().toString();
  
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: '',
    month: currentMonth,
    year: currentYear,
    location: '',
  });
  const [editingTrip, setEditingTrip] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTrips();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [trips, filters]);

  const fetchTrips = async () => {
    try {
      const response = await api.get(`${API}/trips`);
      setTrips(response.data);
      setFilteredTrips(response.data);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...trips];

    if (filters.date) {
      filtered = filtered.filter((trip) => trip.date === filters.date);
    } else if (filters.month && filters.year) {
      filtered = filtered.filter(
        (trip) =>
          trip.date.startsWith(`${filters.year}-${filters.month.padStart(2, '0')}`)
      );
    } else if (filters.year) {
      filtered = filtered.filter((trip) => trip.date.startsWith(filters.year));
    }

    if (filters.location) {
      const searchTerm = filters.location.toLowerCase();
      filtered = filtered.filter(
        (trip) =>
          trip.origin.name.toLowerCase().includes(searchTerm) ||
          trip.origin.address.toLowerCase().includes(searchTerm) ||
          trip.destination.name.toLowerCase().includes(searchTerm) ||
          trip.destination.address.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredTrips(filtered);
    // Hide filters after applying
    if (filters.date || filters.month || filters.year || filters.location) {
      setShowFilters(false);
    }
  };

  const handleDelete = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;

    try {
      await api.delete(`${API}/trips/${tripId}`);
      toast.success('Trip deleted successfully');
      fetchTrips();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Failed to delete trip');
    }
  };

  const handleUpdate = async (tripId, updates) => {
    try {
      await api.put(`${API}/trips/${tripId}`, updates);
      toast.success('Trip updated successfully');
      setEditingTrip(null);
      fetchTrips();
    } catch (error) {
      console.error('Error updating trip:', error);
      toast.error('Failed to update trip');
    }
  };

  const totalMiles = filteredTrips.reduce((sum, trip) => sum + trip.distance_miles, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-[#14B8A6] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 fade-in">
      {/* Filters */}
      <div className="glass-card p-4 md:p-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-filters"
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-[#14B8A6]" />
            <h2 className="text-lg md:text-xl font-bold">Filters</h2>
          </div>
          <span className="text-[#14B8A6]">{showFilters ? '▲' : '▼'}</span>
        </button>
        
        {showFilters && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">Date</label>
            <input
              type="date"
              data-testid="filter-date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value, month: '', year: '' })}
              className="glass-input w-full text-sm"
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">Month</label>
            <select
              data-testid="filter-month"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value, date: '' })}
              className="glass-input w-full text-sm"
              disabled={!!filters.date}
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {format(new Date(2024, i), 'MMMM')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">Year</label>
            <select
              data-testid="filter-year"
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value, date: '' })}
              className="glass-input w-full text-sm"
              disabled={!!filters.date}
            >
              {[2025, 2024, 2023, 2022].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">Location</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                data-testid="filter-location"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="glass-input w-full pl-10 text-sm"
                placeholder="Search location..."
              />
            </div>
          </div>
            </div>
            {(filters.date || filters.month || filters.year || filters.location) && (
              <button
                onClick={() => setFilters({ date: '', month: '', year: '', location: '' })}
                data-testid="clear-filters"
                className="text-xs md:text-sm text-[#14B8A6] hover:underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="glass-card p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs md:text-sm">
              {filters.month && filters.year && !filters.date && !filters.location
                ? `${format(new Date(filters.year, filters.month - 1), 'MMMM yyyy')}`
                : 'Filtered Results'}
            </p>
            <p className="text-2xl md:text-3xl font-bold text-[#14B8A6]">{filteredTrips.length}</p>
            <p className="text-xs text-gray-500">trips</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs md:text-sm">Total Miles</p>
            <p className="text-2xl md:text-3xl font-bold text-[#06B6D4]">{totalMiles.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Trips List */}
      <div className="space-y-3 md:space-y-4">
        {filteredTrips.length === 0 ? (
          <div className="glass-card p-8 md:p-12 text-center">
            <MapPin className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-base md:text-lg">No trips found</p>
            <p className="text-gray-500 text-xs md:text-sm mt-2">
              {trips.length === 0
                ? 'Create your first trip to get started'
                : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          filteredTrips.map((trip) => {
            const tripDate = new Date(trip.date);
            const firstStop = trip.stops && trip.stops.length > 0 ? trip.stops[0].name : trip.destination.name;
            
            return (
              <div key={trip.id} data-testid={`trip-${trip.id}`} className="glass-card p-4 md:p-6">
                {editingTrip === trip.id ? (
                  <EditTripForm
                    trip={trip}
                    onSave={(updates) => handleUpdate(trip.id, updates)}
                    onCancel={() => setEditingTrip(null)}
                  />
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg md:text-xl font-bold truncate">
                          {format(tripDate, 'd MMMM yyyy')}
                        </h3>
                        <p className="text-sm md:text-base text-gray-400 truncate">{firstStop}</p>
                      </div>
                      <div className="flex items-center gap-1 md:gap-2 flex-shrink-0 ml-2">
                        <button
                          onClick={() => setEditingTrip(trip.id)}
                          data-testid={`edit-trip-${trip.id}`}
                          className="p-2 hover:bg-[rgba(20,184,166,0.2)] rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3 h-3 md:w-4 md:h-4 text-[#14B8A6]" />
                        </button>
                        <button
                          onClick={() => handleDelete(trip.id)}
                          data-testid={`delete-trip-${trip.id}`}
                          className="p-2 hover:bg-[rgba(239,68,68,0.2)] rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3 h-3 md:w-4 md:h-4 text-red-400" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-[rgba(20,184,166,0.2)]">
                      <p className="text-xs md:text-sm text-gray-500">Distance</p>
                      <p className="text-xl md:text-2xl font-bold text-[#14B8A6]">
                        {trip.distance_miles.toFixed(2)} <span className="text-xs md:text-sm">mi</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const EditTripForm = ({ trip, onSave, onCancel }) => {
  const [date, setDate] = useState(trip.date);
  const [origin, setOrigin] = useState(trip.origin);
  const [destination, setDestination] = useState(trip.destination);
  const [stops, setStops] = useState(trip.stops || []);
  const [notes, setNotes] = useState(trip.notes || '');
  const [recalculating, setRecalculating] = useState(false);

  const addStop = () => {
    setStops([...stops, null]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStop = (index, location) => {
    const newStops = [...stops];
    newStops[index] = location;
    setStops(newStops);
  };

  const calculateRouteDistance = async () => {
    if (!origin || !destination) return 0;
    
    // Check if all locations have coordinates
    if (!origin.lat || !origin.lon || !destination.lat || !destination.lon) {
      console.error('Missing coordinates for origin or destination');
      return 0;
    }

    try {
      // Build coordinate list
      const coordinates = [origin];
      
      // Add stops that have valid coordinates
      stops.forEach(stop => {
        if (stop && stop.lat && stop.lon) {
          coordinates.push(stop);
        }
      });
      
      coordinates.push(destination);

      // Calculate using OSRM - format: lon,lat (note: OSRM uses lon,lat not lat,lon)
      const coords = coordinates.map(loc => `${loc.lon},${loc.lat}`).join(';');
      const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=false`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.code === 'Ok' && data.routes && data.routes[0]) {
        const distanceMeters = data.routes[0].distance;
        const distanceMiles = distanceMeters * 0.000621371;
        return Math.round(distanceMiles * 100) / 100;
      } else {
        console.error('OSRM error:', data);
        return 0;
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      return 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    onSave({ 
      date, 
      distance_miles: trip.distance_miles, // Keep current distance unless recalculated
      notes,
      origin,
      destination,
      stops
    });
  };

  const handleRecalculate = async () => {
    // Validate that we have proper locations with coordinates
    if (!origin || !destination) {
      toast.error('Origin and destination required');
      return;
    }
    
    if (!origin.lat || !origin.lon) {
      toast.error('Origin location needs coordinates. Please reselect origin.');
      return;
    }
    
    if (!destination.lat || !destination.lon) {
      toast.error('Destination location needs coordinates. Please reselect destination.');
      return;
    }
    
    setRecalculating(true);
    const newDistance = await calculateRouteDistance();
    setRecalculating(false);
    
    if (newDistance > 0) {
      toast.success(`Distance recalculated: ${newDistance} miles`);
      // Update the trip with new distance
      onSave({ 
        date, 
        distance_miles: newDistance,
        notes,
        origin,
        destination,
        stops
      });
    } else {
      toast.error('Could not calculate distance. Please check your route locations have valid coordinates.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs md:text-sm font-medium mb-2">Date</label>
        <input
          type="date"
          data-testid="edit-trip-date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="glass-input w-full text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-xs md:text-sm font-medium mb-2 flex items-center gap-1">
          <MapPin className="w-3 h-3 text-[#14B8A6]" />
          Origin
        </label>
        <LocationSearch
          selectedLocation={origin}
          onSelect={setOrigin}
          placeholder="Search for origin..."
          testId="edit-origin"
        />
      </div>

      {stops.map((stop, index) => (
        <div key={index}>
          <label className="block text-xs md:text-sm font-medium mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-[#06B6D4]" />
              Stop {index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeStop(index)}
              data-testid={`remove-edit-stop-${index}`}
              className="text-red-400 hover:text-red-300 transition-colors text-xs"
            >
              Remove
            </button>
          </label>
          <LocationSearch
            selectedLocation={stop}
            onSelect={(loc) => updateStop(index, loc)}
            placeholder={`Search for stop ${index + 1}...`}
            testId={`edit-stop-${index}`}
          />
        </div>
      ))}

      <button
        type="button"
        onClick={addStop}
        data-testid="add-edit-stop"
        className="w-full py-2 border-2 border-dashed border-[rgba(20,184,166,0.3)] rounded-lg text-[#14B8A6] hover:border-[#14B8A6] hover:bg-[rgba(20,184,166,0.1)] transition-colors flex items-center justify-center gap-2 text-sm"
      >
        <Plus className="w-4 h-4" />
        Add Stop
      </button>

      <div>
        <label className="block text-xs md:text-sm font-medium mb-2 flex items-center gap-1">
          <Navigation className="w-3 h-3 text-[#8B5CF6]" />
          Destination
        </label>
        <LocationSearch
          selectedLocation={destination}
          onSelect={setDestination}
          placeholder="Search for destination..."
          testId="edit-destination"
        />
      </div>

      <div>
        <label className="block text-xs md:text-sm font-medium mb-2">Notes</label>
        <textarea
          data-testid="edit-trip-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="glass-input w-full h-20 resize-none text-sm"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          data-testid="save-trip-button"
          disabled={recalculating}
          className="btn-primary px-6 py-2 text-sm"
        >
          Save
        </button>
        <button
          type="button"
          onClick={handleRecalculate}
          disabled={recalculating}
          data-testid="recalculate-button"
          className="p-2 bg-[rgba(20,184,166,0.2)] hover:bg-[rgba(20,184,166,0.3)] border border-[#14B8A6] rounded-lg transition-colors"
          title="Recalculate distance"
        >
          <RefreshCw className={`w-5 h-5 text-[#14B8A6] ${recalculating ? 'animate-spin' : ''}`} />
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="cancel-edit-button"
          className="px-4 py-2 border border-gray-600 rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors text-sm flex-1"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const LocationSearch = ({ selectedLocation, onSelect, placeholder, testId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [savedLocations, setSavedLocations] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchSavedLocations();
  }, []);

  const fetchSavedLocations = async () => {
    try {
      const response = await api.get(`${API}/locations/saved`);
      setSavedLocations(response.data);
    } catch (error) {
      console.error('Error fetching saved locations:', error);
    }
  };

  const searchLocations = async (searchQuery) => {
    if (searchQuery.length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`${API}/locations/search`, {
        query: searchQuery,
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error searching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value) => {
    setQuery(value);
    setShowResults(true);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      searchLocations(value);
    }, 500);
  };

  const handleSelect = async (location) => {
    onSelect(location);
    setQuery(location.name);
    setShowResults(false);

    // Save location
    try {
      await api.post(`${API}/locations`, location);
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const displayResults = query.length >= 3 ? results : savedLocations.slice(0, 5);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          data-testid={`${testId}-search-input`}
          value={selectedLocation ? selectedLocation.name : query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowResults(true)}
          className="glass-input w-full pl-10 text-sm"
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>

      {showResults && (
        <div
          data-testid={`${testId}-results`}
          className="absolute z-10 w-full mt-2 glass-card max-h-64 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center text-gray-400 text-sm">Searching...</div>
          ) : displayResults.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">
              {query.length >= 3 ? 'No results found' : 'Start typing to search...'}
            </div>
          ) : (
            <div>
              {query.length < 3 && savedLocations.length > 0 && (
                <div className="p-2 text-xs text-gray-500 border-b border-[rgba(20,184,166,0.2)]">
                  Recent & Saved Locations
                </div>
              )}
              {displayResults.map((location, index) => (
                <button
                  key={index}
                  type="button"
                  data-testid={`${testId}-result-${index}`}
                  onClick={() => handleSelect(location)}
                  className="w-full p-3 text-left hover:bg-[rgba(20,184,166,0.1)] transition-colors border-b border-[rgba(20,184,166,0.1)] last:border-b-0"
                >
                  <div className="font-medium text-white text-sm md:text-base truncate">{location.name}</div>
                  <div className="text-xs md:text-sm text-gray-400 truncate">{location.address}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripList;
